'use strict';

var react = require('react');
var utilities_assertUnreachable = require('../../utilities/assertUnreachable.js');
var hooks_useCallbackRef = require('../useCallbackRef.js');

const queryReducer = (state, action) => {
    switch (state.status) {
        case 'idle': {
            return handleIdleTransitions(state, action);
        }
        case 'loading': {
            return handleLoadingTransitions(state, action);
        }
        case 'error': {
            return handleErrorTransitions(state, action);
        }
        case 'loaded': {
            return handleLoadedTransitions(state, action);
        }
        default: {
            throw new Error(utilities_assertUnreachable.assertUnreachable(state));
        }
    }
};
const handleIdleTransitions = (state, action) => {
    switch (action.type) {
        case 'fetch': {
            return {
                status: 'loading',
                mode: { status: 'pre', previousState: state },
                query: action.query,
            };
        }
        case 'complete':
        case 'failure':
        case 'reset':
        case 'full-loading':
        case 'retry': {
            return state;
        }
        default: {
            throw new Error(utilities_assertUnreachable.assertUnreachable(action));
        }
    }
};
const handleLoadingTransitions = (state, action) => {
    switch (action.type) {
        case 'fetch': {
            return {
                status: 'loading',
                mode: state.mode,
                query: action.query,
            };
        }
        case 'complete': {
            return { status: 'loaded', query: state.query, result: action.result };
        }
        case 'failure': {
            return {
                status: 'error',
                query: state.query,
                reason: action.reason,
                result: undefined,
            };
        }
        case 'reset': {
            return { status: 'idle', query: undefined, result: undefined };
        }
        case 'full-loading': {
            return state.mode.status === 'pre'
                ? {
                    ...state,
                    mode: { status: 'full', previousState: state.mode.previousState },
                }
                : state;
        }
        case 'retry': {
            return state;
        }
        default: {
            throw new Error(utilities_assertUnreachable.assertUnreachable(action));
        }
    }
};
const handleErrorTransitions = (state, action) => {
    switch (action.type) {
        case 'fetch': {
            return {
                status: 'loading',
                mode: { status: 'pre', previousState: state },
                query: action.query,
            };
        }
        case 'retry': {
            return {
                status: 'loading',
                mode: { status: 'pre', previousState: state },
                query: state.query,
            };
        }
        case 'reset': {
            return { status: 'idle', query: undefined, result: undefined };
        }
        case 'failure':
        case 'complete':
        case 'full-loading': {
            return state;
        }
        default: {
            throw new Error(utilities_assertUnreachable.assertUnreachable(action));
        }
    }
};
const handleLoadedTransitions = (state, action) => {
    switch (action.type) {
        case 'fetch': {
            return {
                status: 'loading',
                mode: { status: 'pre', previousState: state },
                query: action.query,
            };
        }
        case 'reset': {
            return { status: 'idle', query: undefined, result: undefined };
        }
        case 'failure':
        case 'complete':
        case 'full-loading':
        case 'retry': {
            return state;
        }
        default: {
            throw new Error(utilities_assertUnreachable.assertUnreachable(action));
        }
    }
};
const internalToExternalState = (state) => {
    switch (state.status) {
        case 'loading': {
            return state.mode.status === 'pre'
                ? state.mode.previousState
                : {
                    status: 'loading',
                    query: state.query,
                    result: state.mode.previousState?.result,
                };
        }
        case 'idle':
        case 'error':
        case 'loaded': {
            return state;
        }
        default: {
            throw new Error(utilities_assertUnreachable.assertUnreachable(state));
        }
    }
};
const hasInitialQuery = (parameters) => {
    return 'initialQuery' in parameters;
};
const getInitialState = (parameters) => {
    return hasInitialQuery(parameters)
        ? {
            status: 'loading',
            mode: { status: 'full' },
            query: parameters.initialQuery,
        }
        : {
            status: 'idle',
            query: undefined,
            result: undefined,
        };
};
/**
 * Use this hook when you need to fetch data based on a user query.
 * @see https://dev.azure.com/templafy/Templafy/_wiki/wikis/Engineering%20Wiki/1386/useQuery
 */
const useQuery = (parameters) => {
    const [internalState, dispatch] = react.useReducer(queryReducer, getInitialState(parameters));
    const { action } = parameters;
    const currentAction = hooks_useCallbackRef.useCallbackRef(action);
    react.useEffect(() => {
        const handleSearchSuccess = (result, signal) => {
            if (!signal.aborted) {
                dispatch({ type: 'complete', result });
            }
        };
        const handleSearchFailure = (error, signal) => {
            if (!signal.aborted) {
                dispatch({ type: 'failure', reason: error });
            }
        };
        if (internalState.status === 'loading') {
            const controller = new AbortController();
            currentAction
                .current({ query: internalState.query, signal: controller.signal })
                .then((result) => handleSearchSuccess(result, controller.signal))
                .catch((error) => handleSearchFailure(error, controller.signal));
            return () => controller.abort();
        }
        // New action might receive a different query type, so we can't
        // rerun this effect on action change as it might lead to an exception.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalState.query, internalState.status]);
    const dependencies = [
        internalState.status === 'loading' ? internalState.mode : undefined,
        internalState.status,
    ];
    react.useEffect(() => {
        if (internalState.status === 'loading' &&
            internalState.mode.status === 'pre') {
            const timeoutId = setTimeout(() => dispatch({ type: 'full-loading' }), 200);
            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
    return {
        fetch: react.useRef((query) => dispatch({ type: 'fetch', query })).current,
        reset: react.useRef(() => dispatch({ type: 'reset' })).current,
        retry: react.useRef(() => dispatch({ type: 'retry' })).current,
        state: internalToExternalState(internalState),
    };
};

exports.useQuery = useQuery;
//# sourceMappingURL=useQuery.js.map
